/**
 * HOW TO READ CHART ANALYTICA — the interpretation layer, as data rather than as prose in JSX.
 *
 * <h2>Why it is a module and not comments in the dialog</h2>
 *
 * Fifteen dimensions and a dozen recurring statistical terms, each needing the same three
 * things said about it. Written inline, the explanation of a Wilson interval would appear in
 * four tabs in four slightly different wordings, and the day one of them was corrected the
 * other three would be wrong. Here it is said once and shown wherever the term appears.
 *
 * <h2>Three fields, and the third is the one that earns its place</h2>
 *
 * <pre>
 *   how        how to READ the numbers in this section
 *   practical  what it means for a trade -- the reason an operator is looking
 *   trap       the MISREADING, named. The field this dashboard most needs.
 * </pre>
 *
 * A metric with no trap is rare in this panel: nearly every figure here has a plausible wrong
 * reading that is more flattering than the right one, which is exactly why the engine reports
 * baselines, intervals and in-sample warnings at all. Stating the trap turns a number a reader
 * might over-trust into one they can argue with.
 */

/** The cross-cutting terms, each explained once and shown wherever it appears. */
export const METRICS = {
  winRate: {
    term: 'Win rate / Success',
    how: 'The share of RESOLVED signals that went the signalled way over the forward horizon. '
      + 'Unresolved signals — those whose horizon runs past the bar you clicked — are not in '
      + 'the numerator or the denominator.',
    practical: 'On its own it tells you almost nothing about whether a setup is worth taking. '
      + 'A 30% win rate with a 4:1 payoff is a good book; a 70% win rate that gives it all back '
      + 'on the losers is not.',
    trap: 'Reading it without the EDGE column beside it. If the instrument closes higher 60% of '
      + 'the time anyway, a long signal winning 54% is WORSE than buying at random — and the '
      + 'raw rate looks like a respectable result.',
  },
  baseline: {
    term: 'Baseline and Edge',
    how: 'The baseline is how often price simply closed the signalled way over the same horizon, '
      + 'measured across every bar of the scan. Edge is the signal\'s rate minus that baseline.',
    practical: 'Edge is the number to act on. It is what the signal added over doing nothing on '
      + 'the same instrument over the same period.',
    trap: 'A large positive edge on a handful of observations is noise. Read the "vs baseline" '
      + 'verdict, which asks whether the interval clears the baseline rather than whether the '
      + 'number does.',
  },
  wilson: {
    term: 'Wilson 95% interval',
    how: 'The range the true rate plausibly sits in, given how many observations there were. '
      + 'Narrow means well measured; wide means the sample is small whatever the midpoint says.',
    practical: 'Two setups showing 70% are not the same setup if one is 70% of 200 and the other '
      + 'is 70% of 7. The interval is how you tell them apart at a glance — and if two intervals '
      + 'OVERLAP, the data cannot say which setup is better.',
    trap: 'Treating the midpoint as the answer. Three wins from three reads as 100%, and its '
      + 'interval still reaches down past 40% — which is the honest statement about three trades.',
  },
  unresolved: {
    term: 'Unresolved',
    how: 'A signal that fired too close to the bar you clicked for its forward horizon to have '
      + 'elapsed. It is counted separately and excluded from every rate.',
    practical: 'Expect a few on any scan ending near the right-hand edge. They are the live '
      + 'setups — the ones still playing out.',
    trap: 'Assuming they failed. Folding them in would make every recent signal look bad for the '
      + 'sole reason that time has not passed, which would make the most interesting part of the '
      + 'chart look like the worst.',
  },
  hold: {
    term: 'Hold rate',
    how: 'Of the bars that TESTED a boundary — reached it from inside — the share that were '
      + 'turned back rather than closing through. Penetration and reversion are mutually '
      + 'exclusive on one bar at one level, so the two sum to the tests.',
    practical: 'This is the level\'s strength as support or resistance, stated as a probability. '
      + 'A high hold rate says fade the touch; a low one says trade the break.',
    trap: 'Comparing hold rates across layers without looking at the test count. An outer layer '
      + 'is tested far less often, so its rate moves a long way on a handful of bars.',
  },
  boundary: {
    term: 'Boundary layers b0–b3',
    how: 'b0 is the channel\'s measured edge. b1 sits one band height beyond it, b2 two and b3 '
      + 'three — the same lines the chart draws as the flanking band and the solid and dotted '
      + 'guides.',
    practical: 'A ladder of targets and of stops. Price reaching b1 has travelled the channel\'s '
      + 'own height beyond it, which is a move the structure itself sized.',
    trap: 'Reading the layers as fixed prices. They are MULTIPLES of the channel\'s height, so '
      + 'the same layer is a few points wide on a quiet cycle and many on a volatile one.',
  },
  zone: {
    term: 'Zones 1, 2 and 3',
    how: 'Where price stood when the signal fired. Zone 1 is inside the channel, Zone 2 between '
      + 'the edge and b1, Zone 3 beyond b1.',
    practical: 'A reversal signal fired in Zone 3 is a signal against an extended move; one in '
      + 'Zone 1 is a signal inside the range. They are different trades, and this table is where '
      + 'you find out which the instrument has been paying for.',
    trap: 'Assuming the outer zones are always better because they look more extreme. Check the '
      + 'edge column — on some instruments the extension keeps going.',
  },
  equator: {
    term: 'The equator',
    how: 'The midline of the channel. A PENETRATION is a close crossing it; a REVERSION is a bar '
      + 'that reached it and closed back on the side it came from.',
    practical: 'The midline is where a range-bound instrument spends most of its time, so a high '
      + 'penetration rate says the channel is being traversed rather than respected — momentum '
      + 'conditions. A high reversion rate says the midline is acting as a magnet and a pivot.',
    trap: 'Reading the counts as a state rather than as events. Each one is a CROSSING: a move '
      + 'that sat above the midline for forty sessions is counted once, not forty times.',
  },
  excursion: {
    term: 'Excursion depth',
    how: 'How far beyond the channel edge price travelled during a section, in band heights. '
      + 'Counted once per section, at its furthest.',
    practical: 'This is your target distribution. If 30% of sections reach 2x, a 2x target is '
      + 'hit three times in ten — which is what tells you whether to scale out at 1x.',
    trap: 'Reading the tiers as cumulative. They are not: "2x" means it reached 2x and not 3x. '
      + 'The "reached at least 1x" row below the table is the cumulative reading.',
  },
  correlation: {
    term: 'Correlation (r)',
    how: 'Between −1 and +1. Zero means no linear relationship; the sign says which way it runs. '
      + 'Squaring it gives the share of variance explained, which is always less than it feels.',
    practical: 'r = 0.5 is a real but loose relationship — useful for sizing an expectation, not '
      + 'for predicting a single outcome.',
    trap: 'Reading r = 0.3 as "30% of the time". It is not a percentage of anything; r² = 0.09 '
      + 'is the share of variance, which is under a tenth.',
  },
  relativeVolume: {
    term: 'Relative volume',
    how: 'This bar\'s volume against the average of the twenty before it, EXCLUDING itself. '
      + '1.0x is an ordinary day, 1.3x or more is expansion, 0.8x or less is a dry-up.',
    practical: 'Expansion on a breakout is participation; a dry-up on a retracement is an absence '
      + 'of sellers. Those are the two readings a volume filter is usually built on.',
    trap: 'Expecting the filter to help. The table reports the LIFT it would have given on this '
      + 'range, and on some instruments it is negative — the quiet signals resolved better.',
  },
  inSample: {
    term: 'In-sample',
    how: 'A figure fitted to the very data it is then measured against.',
    practical: 'Useful for RANKING within this chart: the setups the score likes are the ones '
      + 'whose characteristics paid here.',
    trap: 'Treating it as a probability or carrying it to another instrument. The components were '
      + 'chosen because they separated these trades, so the separation is arithmetic rather than '
      + 'evidence. Nothing in-sample is a forecast.',
  },
  horizon: {
    term: 'Forward horizon',
    how: 'The number of bars after a signal over which its outcome is judged. Ten by default.',
    practical: 'It defines what "worked" means here. A signal that paid in three bars and gave '
      + 'it back by ten is recorded as a failure, which is the right answer for a ten-bar hold '
      + 'and the wrong one for a scalp.',
    trap: 'Overlapping windows. Signals cluster, so two four bars apart share most of their '
      + 'window — which is why this panel reports intervals rather than p-values.',
  },
};

/**
 * Per-dimension guidance, keyed by the dimension number the dashboard prints.
 *
 * <p>`metrics` names the glossary entries that apply, so a reader who opens one section's guide
 * gets the terms it uses without the panel repeating them in every section.
 */
export const GUIDANCE = {
  1: {
    how: 'The shape of the range you scanned: how many bars, the extremes, the mix of up and '
      + 'down bars, and the 50/200 regime at the bar you clicked.',
    practical: 'Read it first. Everything below is conditional on this being the market you '
      + 'thought you were looking at — a scan ending inside a bear phase will produce different '
      + 'numbers from one ending at a high, and this is where you see which you have.',
    trap: 'Ignoring the trend state when it says UNKNOWN. That means under 200 bars, not a '
      + 'neutral market, and every regime-sensitive figure below rests on nothing.',
    metrics: [],
  },
  2: {
    how: 'For every channel in scope, how far price ran beyond the edge before the cycle ended — '
      + 'tiered at one, two and three band heights, long side and short side separately.',
    practical: 'Your target distribution, and the single most directly tradable table here. The '
      + '"reached at least 1x" row is the probability a 1x target fills.',
    trap: 'Reading the long and short columns as symmetric. On a trending instrument they are '
      + 'usually not, and taking the average of the two describes neither.',
    metrics: ['excursion', 'boundary'],
  },
  3: {
    how: 'Closes that crossed the midline, split by the side price approached from.',
    practical: 'A high penetration rate per touch says the midline is not holding — the channel '
      + 'is being traversed, which favours continuation entries over fades.',
    trap: 'Comparing the raw counts between two runs of different lengths. Use the rate per '
      + 'touch, which has the number of opportunities in its denominator.',
    metrics: ['equator'],
  },
  4: {
    how: 'Bars that reached the midline and closed back on the side they came from.',
    practical: 'The midline as a pivot. Read against dimension 3: if reversions dominate, the '
      + 'midline is a level to fade; if penetrations do, it is a level to trade through.',
    trap: 'Treating a reversion as a reversal. It says the bar was turned back, not that the '
      + 'move ended — the same level is often penetrated a few bars later.',
    metrics: ['equator'],
  },
  5: {
    how: 'Every CTS upside reversal in the scan, grouped by where price stood when it fired, with '
      + 'the share that resolved upward and how that compares with the baseline.',
    practical: 'This is where you learn which zone the timing study has been paying in on THIS '
      + 'instrument. A zone with a positive edge and a verdict of "above" is a setup worth '
      + 'waiting for.',
    trap: '"Inconclusive" does not mean bad. It means the sample cannot separate it from chance — '
      + 'which is a reason to keep watching it, not a reason to discard it.',
    metrics: ['winRate', 'baseline', 'wilson', 'zone', 'unresolved', 'horizon'],
  },
  6: {
    how: 'The same, the other way: downside reversals by zone.',
    practical: 'Compare it against dimension 5. A long-biased instrument will usually show a '
      + 'weaker short side, and the EDGE column is what tells you whether that is the instrument '
      + 'or the signal.',
    trap: 'Expecting the two sides to mirror. They rarely do, and the asymmetry is itself a '
      + 'finding about the instrument.',
    metrics: ['winRate', 'baseline', 'wilson', 'zone', 'unresolved'],
  },
  7: {
    how: 'How often a CTS point had an AGREEING RSI divergence beside it, and whether those '
      + 'points resolved better than the ones without.',
    practical: 'The LIFT row is the whole dimension: it is what a divergence filter would have '
      + 'been worth on this range. Positive means requiring the divergence improves the book; '
      + 'negative means it would have discarded the better signals.',
    trap: 'Acting on a large lift computed from a handful of concurrent points. Check the n '
      + 'column — divergence and CTS rarely coincide often, and a lift from five observations '
      + 'is a coincidence with a decimal point.',
    metrics: ['winRate', 'baseline', 'wilson'],
  },
  8: {
    how: 'Whether a taller channel precedes a bigger move. Three correlations, because each alone '
      + 'misleads differently — the panel names the one to read.',
    practical: 'If the RELATIVE reading is positive, channel height is a usable size estimate: a '
      + 'wide cycle warrants a wider target and a wider stop. The quartile table underneath is '
      + 'the same question answered without a coefficient.',
    trap: 'Reading the ABSOLUTE number. On a long chart both the channel and the move grow with '
      + 'the price level, so it comes back large whether or not there is a relationship.',
    metrics: ['correlation', 'excursion'],
  },
  9: {
    how: 'Whether standing further from the midline when a signal fires makes a snap-back toward '
      + 'it more likely. Bucketed as well as correlated.',
    practical: 'The buckets are the actionable form. If reversion success climbs with distance, '
      + 'you have a stretched-rubber-band condition and a reason to wait for extension before '
      + 'taking a mean-reversion signal.',
    trap: 'Trusting the coefficient over the buckets. The relationship is often non-monotone — '
      + 'it strengthens to the channel edge and then flattens or collapses once price has broken '
      + 'out entirely — and a single r cannot show that.',
    metrics: ['correlation', 'wilson', 'equator'],
  },
  10: {
    how: 'Every swing in the scan, and how deep its retracement got before the move resumed or '
      + 'was invalidated. "Completed" means price reached at least that depth.',
    practical: 'Your entry-level distribution. If 68% of bullish swings reach 61.8%, a limit '
      + 'there fills two times in three — and the 23.6% row tells you how often a shallow '
      + 'retracement is all you get.',
    trap: 'Reading the rows as independent. They are cumulative by construction: anything that '
      + 'reached 61.8% also reached 50%, so the percentages must fall as the depth increases.',
    metrics: ['wilson'],
  },
  11: {
    how: 'How often price closed THROUGH each boundary layer, and how often it was turned back '
      + 'there. HOLD is the share of tests that were rejected.',
    practical: 'A map of where this instrument\'s structure actually holds. Hold rates that rise '
      + 'with distance say the outer layers are genuine exhaustion levels; flat ones say the '
      + 'ladder is not doing much beyond the first edge.',
    trap: 'Reading an outer layer\'s rate as firmly as an inner one. b3 is tested a fraction as '
      + 'often, so its number moves on a handful of bars.',
    metrics: ['boundary', 'hold'],
  },
  12: {
    how: 'The rejection half of the same table — a touch of the layer that closed back inside.',
    practical: 'Where to place a fade, and where to put the stop behind it.',
    trap: 'Counting a reversion as a trade. It is one bar\'s behaviour at a level, not an '
      + 'outcome; what happened next is not in this table.',
    metrics: ['boundary', 'hold'],
  },
  13: {
    how: 'The two studies held against each other: how often they agree, how often they conflict, '
      + 'and whether the RSI reading at the signal bar says anything about the outcome.',
    practical: 'If concurrence is low, the two are largely independent on this instrument — which '
      + 'means a confluence filter built on them would discard almost everything, and also that '
      + 'they are not duplicating each other.',
    trap: 'Expecting a correlation near zero to be a failure. Two independent studies are more '
      + 'useful together than two that always agree; what matters is whether the agreement, when '
      + 'it happens, improves the outcome.',
    metrics: ['correlation'],
  },
  14: {
    how: 'Relative volume across the range, on the bars where a boundary event happened, and on '
      + 'the CTS signals — with the lift a volume filter would have given.',
    practical: 'The commentary line is the conclusion. Use it to decide whether volume belongs in '
      + 'your entry checklist for this instrument at all.',
    trap: 'Assuming expansion is always confirmation. The lift is negative on some instruments, '
      + 'which means the quiet signals were the better ones and the filter would have cost you.',
    metrics: ['relativeVolume', 'winRate'],
  },
  15: {
    how: 'A 0–100 rating per signal, built by adding up what each component was MEASURED to be '
      + 'worth on this scan. 50 means the components said nothing either way.',
    practical: 'A ranking. Within this chart, the High-tier setups are the ones whose '
      + 'characteristics paid — useful for deciding which of several live signals to size up.',
    trap: 'Treating it as a probability, or carrying it to another chart. It is fitted to the '
      + 'points it ranks, so the separation between High and Low is arithmetic. If High and Low '
      + 'resolve at the same rate, the rating is ranking nothing however carefully it was built.',
    metrics: ['inSample', 'winRate', 'wilson'],
  },
};

/** The guidance for one dimension, or null. */
export function guidanceFor(id) {
  return GUIDANCE[id] || null;
}

/** The glossary entries a dimension's guidance refers to, in the order it names them. */
export function metricsFor(id) {
  const g = GUIDANCE[id];
  if (!g) return [];
  return g.metrics.map(k => METRICS[k]).filter(Boolean);
}

export default GUIDANCE;
